import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../../../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../components/ui/card";
import { Badge } from "../../../components/ui/badge";
import { Progress } from "../../../components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../components/ui/tabs";
import {
  TrendingUp,
  Wallet,
  BarChart3,
  Bell,
  Settings,
  LogOut,
  Home,
  Building2,
  DollarSign,
  Users,
  FileText,
  Plus,
  Eye,
  Edit,
  Clock,
  CheckCircle,
  Info,
  Upload,
  AlertCircle,
  X,
  Menu
} from "lucide-react";
import { useAuth } from "../../../contexts/AuthContext";
import { useConvexProjects, useConvexInvestments, useConvexAuth, useConvexProjectSales } from "../../../services/convexService";
import { Id } from "../../../../backend/_generated/dataModel";
import { toast } from "sonner";
import NotificationBadge from "../../../features/notifications/components/NotificationBadge";
import ProjectHolderNotificationsPreview from "../components/ProjectHolderNotificationsPreview";
import CurrencyDisplay from "../../../components/currency/CurrencyDisplay";
import ProjectSaleModal from "../../../features/project-sales/components/ProjectSaleModal";
import { useLanguage } from "../../../contexts/LanguageContext";

const ProjectHolderDashboard = () => {
  const [activeTab, setActiveTab] = useState("overview");
  const [selectedInvestor, setSelectedInvestor] = useState<any>(null);
  const [showInvestorModal, setShowInvestorModal] = useState(false);
  const [investorDetails, setInvestorDetails] = useState<any>(null);
  const [investorNames, setInvestorNames] = useState<Record<string, string>>({});
  const [allInvestorDetails, setAllInvestorDetails] = useState<Record<string, any>>({});
  const [showProjectSaleModal, setShowProjectSaleModal] = useState(false);
  const [selectedProjectForSale, setSelectedProjectForSale] = useState<any>(null);
  const { user, logout } = useAuth();
  const { getProjectsByOwner, createProject } = useConvexProjects();
  const { getInvestmentsByProjectOwner, approveInvestmentByProjectHolder, rejectInvestment } = useConvexInvestments();
  const { getUser } = useConvexAuth();
  const { createProjectSale } = useConvexProjectSales();
  const navigate = useNavigate();
  const { language, t } = useLanguage();

  // Load user's projects - this hook is called at the top level
  const userProjects = user ? getProjectsByOwner(user.id as Id<"users">) : null;

  // Load investments for user's projects - this hook is also called at the top level
  const userInvestments = user ? getInvestmentsByProjectOwner(user.id as Id<"users">) : null;

  // Calculate stats based on current projects and investments
  const totalRaised = userProjects && userProjects.length > 0
    ? userProjects.reduce((acc, proj) => acc + (proj.currentFunding || 0), 0)
    : 0;

  const totalInvestors = userInvestments
    ? [...new Set(userInvestments.map(inv => inv.investorId))].length
    : 0;

  const avgCompletion = userProjects && userProjects.length > 0
    ? Math.round(userProjects.reduce((acc, proj) => acc + ((proj.currentFunding || 0) / (proj.fundingGoal || 1)) * 100, 0) / userProjects.length)
    : 0;

  // Fetch investor details when userInvestments change
  useEffect(() => {
    const fetchInvestorDetails = async () => {
      if (userInvestments && userInvestments.length > 0) {
        const names: Record<string, string> = {};
        const details: Record<string, any> = {};
        for (const investment of userInvestments) {
          try {
            const investor = await getUser({ userId: investment.investorId });
            if (investor) {
              names[investment.investorId] = investor.name || `Investor ${investment.investorId}`;
              details[investment.investorId] = {
                name: investor.name,
                email: investor.email,
                investmentCapacity: investor.investmentCapacity,
                // Add other fields as needed
              };
            } else {
              names[investment.investorId] = `Investor ${investment.investorId}`;
              details[investment.investorId] = {
                name: `Investor ${investment.investorId}`,
                email: 'N/A',
                investmentCapacity: 'N/A',
              };
            }
          } catch (error) {
            console.error(`Error fetching investor ${investment.investorId}:`, error);
            names[investment.investorId] = `Investor ${investment.investorId}`;
            details[investment.investorId] = {
              name: `Investor ${investment.investorId}`,
              email: 'N/A',
              investmentCapacity: 'N/A',
            };
          }
        }
        setInvestorNames(names);
        setAllInvestorDetails(details);
      }
    };

    fetchInvestorDetails();
  }, [userInvestments, getUser]);

  const handleLogout = () => {
    logout();
    navigate("/home");
  };

  const handleApproveInvestment = async (investmentId: Id<"investments">) => {
    try {
      await approveInvestmentByProjectHolder({ investmentId, approverId: user?.id as Id<"users"> });
      toast.success("Investment approved successfully");
    } catch (error: any) {
      toast.error(error.message || "Failed to approve investment");
    }
  };

  const handleRejectInvestment = async (investmentId: Id<"investments">) => {
    try {
      await rejectInvestment({ investmentId, rejectorId: user?.id as Id<"users">, reason: "Project holder rejected" });
      toast.success("Investment rejected successfully");
    } catch (error: any) {
      toast.error(error.message || "Failed to reject investment");
    }
  };

  const handleViewInvestor = async (investorId: Id<"users">) => {
    try {
      const investor = await getUser({ userId: investorId });
      if (investor) {
        setInvestorDetails(investor);
        setShowInvestorModal(true);
      } else {
        toast.error("Investor information not found");
      }
    } catch (error) {
      console.error("Error fetching investor details:", error);
      toast.error("Failed to fetch investor information");
    }
  };

  const handleSellProject = (project: any) => {
    setSelectedProjectForSale(project);
    setShowProjectSaleModal(true);
  };

  const handleProjectSaleCreated = () => {
    // Refresh the projects after a sale is created
    // In a real implementation, we might want to refetch data
    toast.success("Project sale listing created successfully!");
    setShowProjectSaleModal(false);
  };

  // Helper function to get status badge
  const getStatusBadge = (status: string) => {
    switch (status) {
      case "approved":
        return <Badge variant="default">{t("dashboard.projectholder.status.approved")}</Badge>;
      case "pending_approval":
        return <Badge variant="secondary">{t("dashboard.projectholder.status.pending_approval")}</Badge>;
      case "draft":
        return <Badge variant="outline">{t("dashboard.projectholder.status.draft")}</Badge>;
      case "funded":
        return <Badge variant="default">{t("dashboard.projectholder.status.funded")}</Badge>;
      case "completed":
        return <Badge variant="default">{t("dashboard.projectholder.status.completed")}</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  // Helper function to get investment status badge
  const getInvestmentStatusBadge = (status: string) => {
    switch (status) {
      case "confirmed":
        return <Badge variant="default">{t("dashboard.investor.status.confirmed")}</Badge>;
      case "pending":
        return <Badge variant="secondary">{t("dashboard.investor.status.pending")}</Badge>;
      case "project_holder_approved":
        return <Badge variant="secondary">{t("dashboard.investor.status.project_holder_approved")}</Badge>;
      case "admin_approved":
        return <Badge variant="secondary">{t("dashboard.investor.status.admin_approved")}</Badge>;
      case "rejected":
        return <Badge variant="destructive">{t("dashboard.investor.status.rejected")}</Badge>;
      case "refunded":
        return <Badge variant="outline">{t("dashboard.investor.status.refunded")}</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  // Helper function to get investment status display text
  const getInvestmentStatusDisplay = (status: string) => {
    switch (status) {
      case "pending":
        return t("dashboard.investor.status.pending");
      case "project_holder_approved":
        return t("dashboard.investor.status.project_holder_approved");
      case "admin_approved":
        return t("dashboard.investor.status.admin_approved");
      case "confirmed":
        return t("dashboard.investor.status.confirmed");
      case "rejected":
        return t("dashboard.investor.status.rejected");
      case "refunded":
        return t("dashboard.investor.status.refunded");
      default:
        return status;
    }
  };

  return (
    <div className="min-h-screen bg-background" dir={language === "ar" ? "rtl" : "ltr"}>
      {/* Mobile menu button */}
      <div className="lg:hidden fixed top-4 right-4 z-50">
        <Button
          variant="outline"
          size="icon"
          onClick={() => document.getElementById('projectholder-mobile-sidebar')?.classList.toggle('hidden')}
        >
          <Menu className="w-4 h-4" />
        </Button>
      </div>

      {/* Mobile Sidebar */}
      <aside
        id="projectholder-mobile-sidebar"
        className="lg:hidden fixed inset-0 z-40 bg-black/50 hidden"
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            (e.target as HTMLElement).classList.add('hidden');
          }
        }}
      >
        <div className="w-64 h-full bg-card p-4 ml-auto">
          {/* Logo and Text removed from sidebar header */}
          <div className="h-8"></div>

          <nav className="space-y-2 mb-8">
            <Button
              variant={activeTab === "overview" ? "default" : "ghost"}
              className="w-full justify-start gap-3"
              onClick={() => {
                setActiveTab("overview");
                document.getElementById('projectholder-mobile-sidebar')?.classList.add('hidden');
              }}
            >
              <Home className="w-4 h-4" />
              {t("dashboard.projectholder.overview")}
            </Button>
            <Button
              variant={activeTab === "projects" ? "default" : "ghost"}
              className="w-full justify-start gap-3"
              onClick={() => {
                setActiveTab("projects");
                document.getElementById('projectholder-mobile-sidebar')?.classList.add('hidden');
              }}
            >
              <Building2 className="w-4 h-4" />
              {t("dashboard.projectholder.projects")}
            </Button>
            <Button
              variant={activeTab === "investors" ? "default" : "ghost"}
              className="w-full justify-start gap-3"
              onClick={() => {
                setActiveTab("investors");
                document.getElementById('projectholder-mobile-sidebar')?.classList.add('hidden');
              }}
            >
              <Users className="w-4 h-4" />
              {t("dashboard.projectholder.investors")}
            </Button>
            <Button
              variant={activeTab === "notifications" ? "default" : "ghost"}
              className="w-full justify-start gap-3"
              onClick={() => {
                setActiveTab("notifications");
                document.getElementById('projectholder-mobile-sidebar')?.classList.add('hidden');
              }}
            >
              <Bell className="w-4 h-4" />
              {t("dashboard.projectholder.notifications")}
              {user && (
                <NotificationBadge userId={user.id as Id<"users">} />
              )}
            </Button>
            <Button
              variant="ghost"
              className="w-full justify-start gap-3"
              onClick={() => {
                navigate("/settings/project-holder");
                document.getElementById('projectholder-mobile-sidebar')?.classList.add('hidden');
              }}
            >
              <Settings className="w-4 h-4" />
              {t("settings.title")}
            </Button>
          </nav>

          <div className="space-y-2">
            <Link to="/home" onClick={() => document.getElementById('projectholder-mobile-sidebar')?.classList.add('hidden')}>
              <Button variant="outline" className="w-full justify-start gap-3">
                <Home className="w-4 h-4" />
                {t("navigation.home")}
              </Button>
            </Link>
            <Button variant="ghost" className="w-full justify-start gap-3 text-destructive hover:text-destructive" onClick={() => {
              handleLogout();
              document.getElementById('projectholder-mobile-sidebar')?.classList.add('hidden');
            }}>
              <LogOut className="w-4 h-4" />
              {t("common.logout")}
            </Button>
          </div>
        </div>
      </aside>

      {/* Desktop Sidebar */}
      <aside className="fixed right-0 top-0 h-full w-64 bg-card border-l border-border p-4 hidden lg:block" dir={language === "ar" ? "rtl" : "ltr"}>
        {/* Logo and Text removed from sidebar header */}
        <div className="h-8"></div>

        <nav className="space-y-2">
          <Button
            variant={activeTab === "overview" ? "default" : "ghost"}
            className="w-full justify-start gap-3"
            onClick={() => setActiveTab("overview")}
          >
            <Home className="w-4 h-4" />
            {t("dashboard.projectholder.overview")}
          </Button>
          <Button
            variant={activeTab === "projects" ? "default" : "ghost"}
            className="w-full justify-start gap-3"
            onClick={() => setActiveTab("projects")}
          >
            <Building2 className="w-4 h-4" />
            {t("dashboard.projectholder.projects")}
          </Button>
          <Button
            variant={activeTab === "investors" ? "default" : "ghost"}
            className="w-full justify-start gap-3"
            onClick={() => setActiveTab("investors")}
          >
            <Users className="w-4 h-4" />
            {t("dashboard.projectholder.investors")}
          </Button>
          <Button
            variant={activeTab === "notifications" ? "default" : "ghost"}
            className="w-full justify-start gap-3"
            onClick={() => setActiveTab("notifications")}
          >
            <Bell className="w-4 h-4" />
            {t("dashboard.projectholder.notifications")}
            {user && (
              <NotificationBadge userId={user.id as Id<"users">} />
            )}
          </Button>
          <Button
            variant="ghost"
            className="w-full justify-start gap-3"
            onClick={() => navigate("/settings/project-holder")}
          >
            <Settings className="w-4 h-4" />
            {t("settings.title")}
          </Button>
        </nav>

        <div className="absolute bottom-4 right-4 left-4 space-y-2">
          <Link to="/home">
            <Button variant="outline" className="w-full justify-start gap-3">
              <Home className="w-4 h-4" />
              {t("navigation.home")}
            </Button>
          </Link>
          <Button variant="ghost" className="w-full justify-start gap-3 text-destructive hover:text-destructive" onClick={handleLogout}>
            <LogOut className="w-4 h-4" />
            {t("common.logout")}
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:mr-64 p-6" dir={language === "ar" ? "rtl" : "ltr"}>
        <div className="flex items-center justify-between mb-8 w-full">
          <div>
            <h1 className="text-2xl font-bold text-foreground">
              {t("common.welcome")}, {user?.name || t("dashboard.projectholder.title")}
            </h1>
            <p className="text-muted-foreground">
              {t("dashboard.projectholder.subtitle") || t("common.dashboard")}
            </p>
          </div>

          <img
            src="/logo.png"
            alt="Knowvest.plus"
            className="h-16 w-auto object-contain"
          />
        </div>

        {/* Account Status Warning */}
        {user?.status !== "approved" && (
          <Card className="mb-6 border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
                <div>
                  <p className="font-medium text-yellow-800 dark:text-yellow-200">
                    {user?.status === "pending_approval"
                      ? t("status.pending_approval")
                      : user?.status === "rejected"
                        ? t("status.rejected")
                        : user?.status === "suspended" ? t("status.suspended") : t("status.approved")}
                  </p>
                  <p className="text-sm text-yellow-700 dark:text-yellow-300">
                    {user?.status === "pending_approval"
                      ? t("waiting.projectholder.message")
                      : user?.status === "rejected"
                        ? t("waiting.projectholder.reviewProject") // Placeholder
                        : t("waiting.projectholder.verifyReadiness")} // Placeholder
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{t("dashboard.projectholder.totalFunding")}</p>
                  <CurrencyDisplay amount={totalRaised} className="text-2xl font-bold text-foreground" />
                </div>
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                  <Wallet className="w-6 h-6 text-primary" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{t("dashboard.projectholder.projectCount")}</p>
                  <p className="text-2xl font-bold text-foreground">{userProjects ? userProjects.length : 0}</p>
                </div>
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-primary" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{t("dashboard.projectholder.investorCount")}</p>
                  <p className="text-2xl font-bold text-foreground">{totalInvestors}</p>
                </div>
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                  <Users className="w-6 h-6 text-primary" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{t("dashboard.projectholder.avgCompletion")}</p>
                  <p className="text-2xl font-bold text-primary">{avgCompletion}%</p>
                </div>
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-primary" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs Content */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="lg:hidden grid grid-cols-3 w-full mb-8">
            <TabsTrigger value="overview">{t("dashboard.projectholder.overview")}</TabsTrigger>
            <TabsTrigger value="projects">{t("dashboard.projectholder.projects")}</TabsTrigger>
            <TabsTrigger value="investors">{t("dashboard.projectholder.investors")}</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            {/* Active Projects */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>{t("dashboard.projectholder.activeProjects")}</CardTitle>
                  <CardDescription>{t("dashboard.projectholder.activeProjects")}</CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={() => setActiveTab("projects")}>
                  {t("common.viewAll")}
                </Button>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {userProjects && userProjects.length > 0 ? (
                    userProjects.slice(0, 3).map((project) => (
                      <div key={project._id} className="flex items-center gap-4 p-4 bg-muted/50 rounded-lg">
                        <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                          <Building2 className="w-6 h-6 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-foreground">{project.title}</h4>
                          <div className="text-right text-sm font-medium text-foreground mt-2">
                            {Math.round(((project.currentFunding || 0) / (project.fundingGoal || 1)) * 100)}%
                          </div>
                          <Progress
                            value={((project.currentFunding || 0) / (project.fundingGoal || 1)) * 100}
                            className="h-2 mt-1"
                          />
                          <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                            <Info className="w-3 h-3" />
                            {t("common.expectedReturn") || "Expected Return"}: {project.expectedROI || 0}%
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button variant="outline" size="sm" asChild>
                            <Link to={`/projects/${project._id}`}>
                              <Eye className="w-4 h-4 mr-2" />
                              {t("common.view")}
                            </Link>
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-orange-600 hover:text-orange-600 hover:bg-orange-50"
                            onClick={() => handleSellProject(project)}
                          >
                            <DollarSign className="w-4 h-4 mr-2" />
                            {t("projectSale.title") || "Sell Project"}
                          </Button>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      {t("common.noData") || "No projects yet"}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Recent Investors */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>{t("dashboard.projectholder.recentInvestors")}</CardTitle>
                  <CardDescription>{t("dashboard.projectholder.recentInvestors")}</CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={() => setActiveTab("investors")}>
                  {t("common.viewAll")}
                </Button>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {userInvestments && userInvestments.length > 0 ? (
                    userInvestments.slice(0, 3).map((investment, index) => {
                      const investorName = investorNames[investment.investorId] || `Investor ${index + 1}`;

                      return (
                        <div key={index} className="flex items-center gap-4 p-4 bg-muted/50 rounded-lg">
                          <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                            <Users className="w-5 h-5 text-muted-foreground" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium text-foreground">{investorName}</h4>
                            <p className="text-sm text-muted-foreground">{t("common.invested")} <CurrencyDisplay amount={investment.amount || 0} /></p>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="text-right min-w-0">
                              <div className="text-sm font-medium text-primary">{investment.status}</div>
                              <div className="text-xs text-muted-foreground">{t("common.status")}</div>
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleViewInvestor(investment.investorId)}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      {t("common.noData") || "No investments yet"}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="projects">
            <Card>
              <CardHeader>
                <div>
                  <CardTitle>{t("dashboard.projectholder.allProjects")}</CardTitle>
                  <CardDescription>{t("dashboard.projectholder.allProjects")}</CardDescription>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {userProjects && userProjects.length > 0 ? (
                    userProjects.map((project) => (
                      <div key={project._id} className="p-6 border border-border rounded-lg hover:border-primary transition-colors">
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <h4 className="font-semibold text-lg text-foreground">{project.title}</h4>
                            <p className="text-sm text-muted-foreground" dir={language === "ar" ? "rtl" : "ltr"}>{project.description}</p>
                          </div>
                          {getStatusBadge(project.status)}
                        </div>

                        <div className="grid grid-cols-3 gap-4 mb-4">
                          <div>
                            <p className="text-xs text-muted-foreground">{t("common.goal")}</p>
                            <p className="font-medium text-foreground"><CurrencyDisplay amount={project.fundingGoal || 0} /></p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">{t("common.raised")}</p>
                            <p className="font-medium text-primary"><CurrencyDisplay amount={project.currentFunding || 0} /></p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">{t("common.investors")}</p>
                            <p className="font-medium text-foreground">
                              {userInvestments ? userInvestments.filter(inv => inv.projectId === project._id).length : 0}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center justify-between text-sm mb-4 text-muted-foreground">
                          <div className="flex items-center gap-2">
                            <Info className="w-4 h-4" />
                            <span>{t("common.expectedReturn")}: {project.expectedROI || 0}%</span>
                          </div>
                        </div>

                        <Progress
                          value={((project.currentFunding || 0) / (project.fundingGoal || 1)) * 100}
                          className="h-2 mb-4"
                        />

                        <div className="flex items-center gap-2">
                          <Button variant="outline" size="sm" className="gap-2" asChild>
                            <Link to={`/projects/${project._id}`}>
                              <Eye className="w-4 h-4" />
                              {t("common.view")}
                            </Link>
                          </Button>
                          <Button variant="outline" size="sm" className="gap-2" asChild>
                            <Link to={`/projects/${project._id}/edit`}>
                              <Edit className="w-4 h-4" />
                              {t("common.edit")}
                            </Link>
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="gap-2 text-orange-600 hover:text-orange-600 hover:bg-orange-50"
                            onClick={() => handleSellProject(project)}
                          >
                            <DollarSign className="w-4 h-4 mr-2" />
                            {t("projectSale.title")}
                          </Button>
                          <Button variant="outline" size="sm" className="gap-2">
                            <BarChart3 className="w-4 h-4" />
                            {t("dashboard.projectholder.reports")}
                          </Button>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      {t("common.noData") || "No projects yet"}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="investors">
            <Card>
              <CardHeader>
                <CardTitle>{t("dashboard.projectholder.allInvestors")}</CardTitle>
                <CardDescription>{t("dashboard.projectholder.allInvestors")}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {userInvestments && userInvestments.length > 0 ? (
                    userInvestments.map((investment, index) => {
                      return (
                        <div key={index} className="p-4 border border-border rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="font-medium text-foreground">{investorNames[investment.investorId] || `Investor ${index + 1}`}</h4>
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-muted-foreground">#{index + 1}</span>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleViewInvestor(investment.investorId)}
                              >
                                <Eye className="w-4 h-4 mr-1" />
                                {t("common.view")}
                              </Button>
                            </div>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                            <div>
                              <p className="text-xs text-muted-foreground">{t("common.email")}</p>
                              <p className="font-medium">{allInvestorDetails[investment.investorId]?.email || 'N/A'}</p>
                            </div>
                            <div>
                              <p className="text-xs text-muted-foreground">{t("common.amountInvested")}</p>
                              <p className="font-medium text-primary"><CurrencyDisplay amount={investment.amount || 0} /></p>
                            </div>
                            <div>
                              <p className="text-xs text-muted-foreground">{t("common.project")}</p>
                              <p className="font-medium">
                                {userProjects?.find(p => p._id === investment.projectId)?.title || t("common.project")}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-muted-foreground">{t("common.investmentCapacity")}</p>
                              <p className="font-medium">{allInvestorDetails[investment.investorId]?.investmentCapacity?.toLocaleString() || 'N/A'}</p>
                            </div>
                          </div>
                          <div className="mt-2">
                            <p className="text-xs text-muted-foreground">{t("common.status")}</p>
                            <div className="flex items-center justify-between">
                              <span className="text-sm">{getInvestmentStatusDisplay(investment.status)}</span>
                              {getInvestmentStatusBadge(investment.status)}
                            </div>
                          </div>
                          {/* Only show approval/rejection buttons for pending investments */}
                          {investment.status === "pending" && (
                            <div className="flex gap-2 mt-3">
                              <Button
                                size="sm"
                                variant="outline"
                                className="flex-1 gap-2 text-green-600 hover:text-green-600 hover:bg-green-50"
                                onClick={() => handleApproveInvestment(investment._id)}
                              >
                                <CheckCircle className="w-4 h-4" />
                                {t("common.approve")}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="flex-1 gap-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                                onClick={() => handleRejectInvestment(investment._id)}
                              >
                                <AlertCircle className="w-4 h-4" />
                                {t("common.reject")}
                              </Button>
                            </div>
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      {t("common.noData") || "No investors yet"}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>



          <TabsContent value="notifications">
            <Card>
              <CardHeader>
                <CardTitle>{t("dashboard.projectholder.notifications")}</CardTitle>
                <CardDescription>{t("common.notifications")}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {user && (
                    <ProjectHolderNotificationsPreview userId={user.id as Id<"users">} />
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="settings">
            <Card>
              <CardHeader>
                <CardTitle>{t("settings.accountSettings")}</CardTitle>
                <CardDescription>{t("settings.subtitle")}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                    <div>
                      <p className="font-medium text-foreground">{t("settings.personalInfo.title")}</p>
                      <p className="text-sm text-muted-foreground">{t("settings.personalInfo.subtitle")}</p>
                    </div>
                    <Button variant="outline" size="sm" asChild>
                      <Link to="/settings">{t("common.edit")}</Link>
                    </Button>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                    <div>
                      <p className="font-medium text-foreground">{t("settings.password.title")}</p>
                      <p className="text-sm text-muted-foreground">{t("settings.password.subtitle")}</p>
                    </div>
                    <Button variant="outline" size="sm" asChild>
                      <Link to="/settings">{t("common.change")}</Link>
                    </Button>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                    <div>
                      <p className="font-medium text-foreground">{t("settings.notifications.title")}</p>
                      <p className="text-sm text-muted-foreground">{t("settings.notifications.subtitle")}</p>
                    </div>
                    <Button variant="outline" size="sm" asChild>
                      <Link to="/notifications/project-holder">{t("settings.notifications.manage")}</Link>
                    </Button>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                    <div>
                      <p className="font-medium text-foreground">{t("settings.currency.title")}</p>
                      <p className="text-sm text-muted-foreground">{t("settings.currency.subtitle")}</p>
                    </div>
                    <Button variant="outline" size="sm" asChild>
                      <Link to="/settings">{t("settings.currency.change")}</Link>
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Investor Detail Modal */}
      {showInvestorModal && investorDetails && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-background rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center border-b p-4">
              <h3 className="text-lg font-semibold">{t("common.investorDetails")}</h3>
              <Button variant="ghost" size="icon" onClick={() => setShowInvestorModal(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>

            <div className="p-6">
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">{t("common.name")}</p>
                    <p className="text-lg font-medium">{investorDetails.name}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">{t("common.email")}</p>
                    <p className="text-base">{investorDetails.email}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">{t("common.phone")}</p>
                    <p className="text-base">{investorDetails.phone || t("common.notAvailable")}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">{t("common.company")}</p>
                    <p className="text-base">{investorDetails.company || t("common.notAvailable")}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">{t("common.investmentCapacity")}</p>
                    <p className="text-base">{investorDetails.investmentCapacity ? <CurrencyDisplay amount={investorDetails.investmentCapacity} /> : t("common.notSpecified")}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">{t("common.expectedReturn")}</p>
                    <p className="text-base">{investorDetails.riskTolerance || t("common.notSpecified")}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">{t("common.investmentExperience")}</p>
                    <p className="text-base">{investorDetails.investmentExperience || t("common.notAvailable")}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">{t("common.occupation")}</p>
                    <p className="text-base">{investorDetails.occupation || t("common.notAvailable")}</p>
                  </div>
                </div>

                {investorDetails.preferredSectors && investorDetails.preferredSectors.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">{t("common.preferredSectors")}</p>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {investorDetails.preferredSectors.map((sector: string, index: number) => (
                        <span
                          key={index}
                          className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm"
                        >
                          {sector}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {investorDetails.bio && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">{t("common.bio")}</p>
                    <p className="text-base mt-1">{investorDetails.bio}</p>
                  </div>
                )}

                {investorDetails.linkedin && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">{t("common.linkedin")}</p>
                    <a
                      href={investorDetails.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      {investorDetails.linkedin}
                    </a>
                  </div>
                )}

                <div>
                  <p className="text-sm font-medium text-muted-foreground">{t("common.status")}</p>
                  <p className="text-base capitalize">{investorDetails.status}</p>
                </div>
              </div>

              <div className="flex justify-end pt-6">
                <Button variant="outline" onClick={() => setShowInvestorModal(false)}>
                  {t("common.close")}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Project Sale Modal */}
      {showProjectSaleModal && selectedProjectForSale && (
        <ProjectSaleModal
          projectId={selectedProjectForSale._id}
          projectTitle={selectedProjectForSale.title}
          isOpen={showProjectSaleModal}
          onClose={() => setShowProjectSaleModal(false)}
          onSaleCreated={handleProjectSaleCreated}
        />
      )}
    </div>
  );
};

export default ProjectHolderDashboard;